export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Guard so a burst of failed requests can't fire several navigations. */
let redirecting = false;
function leave(to: string) {
  if (redirecting) return;
  redirecting = true;
  window.location.assign(to);
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body != null;

  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    leave('/login');
    throw new ApiError(401, null, 'Not signed in');
  }

  if (res.status === 503) {
    const body = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: string } | null;
    if (body?.error === 'setup_required') {
      leave('/setup');
      throw new ApiError(503, body, 'App is not configured yet');
    }
  }

  // Defensive: before the backend returned proper 401s, an expired session
  // produced a 302 to the login page, which fetch follows silently and hands
  // back as 200 text/html. Without this check that becomes an opaque JSON
  // parse error rather than an actionable one.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (res.redirected) {
      leave(res.url);
      throw new ApiError(res.status, null, 'Redirected away from the API');
    }
    const text = await res.text().catch(() => '');
    throw new ApiError(
      res.status,
      text,
      `Expected JSON from ${path}, got "${contentType || 'no content-type'}"`,
    );
  }

  const body = (await res.json()) as unknown;

  if (!res.ok) {
    const message = (body as { error?: string } | null)?.error ?? res.statusText;
    throw new ApiError(res.status, body, message);
  }

  return body as T;
}

export const get = <T,>(path: string) => api<T>(path);

export const post = <T,>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' });

/** Build a query string, dropping empty/undefined values so the URL stays
 *  clean and equal filters produce equal React Query cache keys. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}
